// Every word on the public website, in Arabic.
//
// The site is Arabic only, as its designs are drawn ("Home Visibility" and
// "Pricing" in the Claude Design project), so its copy lives here rather than
// in src/i18n, which holds the till's and the dashboard's two languages
// (decided 2026-10-03, .claude/rules/web.md).
//
// A feature that is not built yet carries the design system's «قريبًا» chip,
// and its numbers are examples; every such feature is listed in
// docs/backlog.md. Four are the exception, shown without the chip at the
// owner's decision (2026-10-03): Ask Bosla, the AI summaries, AI ordering and
// profit per item.
//
// A run marked «like this» is a figure: Rich.svelte sets it in mono, left to
// right, as the design does.

export type Tone = 'ledger' | 'bright' | 'pale' | 'flag' | 'speed';

export const brand = 'بوصلة';
export const soon = 'قريبًا';
export const signUp = 'افتح حسابك مجانًا';
export const tagline = 'كاشير ويب للمطاعم الصغيرة في مصر.';

export const nav = {
	label: 'صفحات الموقع',
	home: 'بوصلة، الصفحة الرئيسية',
	reports: 'التقارير',
	summaries: 'الملخصات',
	orders: 'طلبات واتساب',
	pricing: 'الأسعار',
	faq: 'أسئلة',
	privacy: 'الخصوصية',
	start: 'ابدأ مجانا',
};

export const footer = {
	pages: 'الصفحات',
	legal: 'قانوني',
};

export const hero = {
	eyebrow: 'بوصلة يعني compass — بتقولك انت فين',
	titleStart: 'شوف مطعمك ',
	titleMark: 'كله',
	titleEnd: '،',
	titleLine2: 'حتى وانت مش فيه.',
	lead: 'كل فاتورة بتتطبع على الكاشير بتبقى رقم تقدر تشوفه. تقارير بتتحدّث مع كل بيعة، وتفتحها من أي متصفح — ',
	leadStrong: 'من فواتيرك انت، مش تخمين',
	leadEnd: '.',
	seeReport: 'شوف شكل التقرير',
};

export interface Question {
	text: string;
	scan: string;
	lead: string;
	bars: { label: string; value: number; display: string; tone: Tone }[];
	source: string;
}

export const ask = {
	title: 'اسأل بوصلة',
	note: 'بيقرأ فواتيرك بس',
	example: 'الأرقام مثال.',
	seeking: 'بيدوّر',
	searchingIn: 'بيدوّر في ',
	found: 'لقى الإجابة',
	questions: [
		{
			text: 'إيه أكتر صنف كسّبني الأسبوع اللي فات؟',
			scan: '1,284 فاتورة',
			lead: 'كشري كبير: «18,420» ج من «614» طبق — «21٪» من المبيعات. بعده طاجن فراخ بـ «12,960» ج، والرز بلبن هو اللي بيطلع مع أغلب الأوردرات الكبيرة.',
			bars: [
				{ label: 'كشري كبير', value: 100, display: '18,420', tone: 'ledger' },
				{ label: 'طاجن فراخ', value: 70, display: '12,960', tone: 'pale' },
				{ label: 'رز بلبن', value: 41, display: '7,610', tone: 'pale' },
			],
			source: 'من 1,284 فاتورة · 12–18 سبتمبر · 3 فروع',
		},
		{
			text: 'ليه يوم التلات كان أقل من العادي؟',
			scan: '186 فاتورة يوم التلات',
			lead: 'التلات قفل «4,960» ج، أقل من متوسطك بـ «28٪». الفرق كله بين «1» و«4» الضهر: «38» فاتورة بدل «71» — وفرع المعادي كان مش متصل «52» دقيقة الساعة «2».',
			bars: [
				{ label: 'الضهر العادي', value: 100, display: '71', tone: 'ledger' },
				{ label: 'ضهر التلات', value: 54, display: '38', tone: 'flag' },
				{ label: 'باقي اليوم', value: 96, display: '148', tone: 'pale' },
			],
			source: 'مقارنة بمتوسط آخر 4 تلاتات · فرع المعادي',
		},
		{
			text: 'إمتى أزحم وقت عندي في الأسبوع؟',
			scan: '1,284 فاتورة',
			lead: 'من «8» لـ «10» بالليل: «38٪» من فواتير الأسبوع في ساعتين. الجمعة لوحدها فيها «214» فاتورة في الوقت ده.',
			bars: [
				{ label: '8–9 م', value: 100, display: '262', tone: 'ledger' },
				{ label: '9–10 م', value: 86, display: '226', tone: 'pale' },
				{ label: '2–3 م', value: 47, display: '124', tone: 'pale' },
			],
			source: 'من 1,284 فاتورة · 12–18 سبتمبر · كل الفروع',
		},
	] satisfies [Question, ...Question[]],
};

export const compass = [
	{
		label: 'بتعرف',
		value: 'فين',
		body: 'كل فرع، وكل ساعة، وكل صنف — دلوقتي، مش آخر الشهر.',
		tone: 'paper',
	},
	{
		label: 'بتفهم',
		value: 'ليه',
		body: 'اليوم الوحش ليه كان وحش، والصنف اللي طالع ليه طالع.',
		tone: 'paper',
	},
	{
		label: 'بتقرر',
		value: 'رايح على فين',
		body: 'البوصلة مابتمشيش بدالك. بس عمرك ما هتمشي وانت مش عارف الاتجاه.',
		tone: 'ledger',
	},
] as const;

export type Range = 'day' | 'week' | 'month';

export interface Report {
	note: string;
	figures: { label: string; value: string; sub: string }[];
	items: { name: string; qty: string; share: number; revenue: string }[];
	pay: { label: string; value: number; display: string; tone: Tone }[];
}

export const reports = {
	eyebrow: 'التقارير',
	title: 'الأرقام اللي بتتعب عشان تجمعها، جاهزة.',
	body: 'المبيعات، والأصناف حتى اللي ما اتباعتش، وساعات الزحمة، والكاش والكارت، والإلغاءات، وفتح الدرج من غير بيع — لفرع واحد أو كلهم مع بعض، ليوم أو أسبوع أو شهر أو أي مدة تختارها.',
	example: 'الأرقام اللي تحت مثال.',
	rangeLabel: 'المدة',
	ranges: [
		{ value: 'day', label: 'امبارح' },
		{ value: 'week', label: 'الأسبوع' },
		{ value: 'month', label: 'الشهر' },
	] satisfies { value: Range; label: string }[],
	itemsTitle: 'أكتر الأصناف',
	itemColumns: [
		{ label: 'الصنف', w: 'minmax(0,1.3fr)' },
		{ label: 'اتباع', w: 'minmax(0,.5fr)' },
		{ label: 'النسبة', w: 'minmax(0,1fr)' },
		{ label: 'ج', w: 'minmax(0,.7fr)' },
	],
	payTitle: 'الكاش والكارت',
	payNote:
		'الكاش والكارت مفصولين، فتطابق الدرج والبنك على الرقم. والإلغاءات محسوبة لوحدها، عمرها ما بتدخل في المبيعات.',
	byRange: {
		day: {
			note: 'امبارح · وسط البلد',
			figures: [
				{ label: 'المبيعات', value: '7,340', sub: 'ج صافي' },
				{ label: 'الفواتير', value: '186', sub: 'فاتورة مدفوعة' },
				{ label: 'متوسط الفاتورة', value: '39.5', sub: 'ج' },
				{ label: 'أزحم ساعة', value: '8–9 م', sub: '«41» فاتورة' },
			],
			items: [
				{ name: 'كشري كبير', qty: '92', share: 100, revenue: '2,760' },
				{ name: 'طاجن فراخ', qty: '31', share: 67, revenue: '1,860' },
				{ name: 'رز بلبن', qty: '58', share: 38, revenue: '1,044' },
				{ name: 'سلطة طحينة', qty: '47', share: 19, revenue: '564' },
				{ name: 'بيبسي', qty: '73', share: 13, revenue: '365' },
			],
			pay: [
				{ label: 'كاش', value: 77, display: '5,620', tone: 'ledger' },
				{ label: 'كارت', value: 23, display: '1,720', tone: 'bright' },
				{ label: 'إلغاءات · 3', value: 1, display: '75', tone: 'flag' },
			],
		},
		week: {
			note: '12–18 سبتمبر · كل الفروع',
			figures: [
				{ label: 'المبيعات', value: '86,210', sub: 'ج صافي' },
				{ label: 'الفواتير', value: '1,284', sub: 'فاتورة مدفوعة' },
				{ label: 'متوسط الفاتورة', value: '67.1', sub: 'ج' },
				{ label: 'أزحم ساعة', value: '8–9 م', sub: '«262» فاتورة' },
			],
			items: [
				{ name: 'كشري كبير', qty: '614', share: 100, revenue: '18,420' },
				{ name: 'طاجن فراخ', qty: '216', share: 70, revenue: '12,960' },
				{ name: 'رز بلبن', qty: '423', share: 41, revenue: '7,610' },
				{ name: 'سلطة طحينة', qty: '388', share: 25, revenue: '4,656' },
				{ name: 'بيبسي', qty: '702', share: 19, revenue: '3,510' },
			],
			pay: [
				{ label: 'كاش', value: 75, display: '64,650', tone: 'ledger' },
				{ label: 'كارت', value: 25, display: '21,560', tone: 'bright' },
				{ label: 'إلغاءات · 11', value: 1, display: '410', tone: 'flag' },
			],
		},
		month: {
			note: 'أغسطس · كل الفروع',
			figures: [
				{ label: 'المبيعات', value: '352,900', sub: 'ج صافي' },
				{ label: 'الفواتير', value: '5,318', sub: 'فاتورة مدفوعة' },
				{ label: 'متوسط الفاتورة', value: '66.4', sub: 'ج' },
				{ label: 'أزحم ساعة', value: '8–9 م', sub: '«1,040» فاتورة' },
			],
			items: [
				{ name: 'كشري كبير', qty: '2,540', share: 100, revenue: '76,200' },
				{ name: 'طاجن فراخ', qty: '880', share: 69, revenue: '52,800' },
				{ name: 'رز بلبن', qty: '1,702', share: 40, revenue: '30,640' },
				{ name: 'سلطة طحينة', qty: '1,590', share: 25, revenue: '19,080' },
				{ name: 'بيبسي', qty: '2,811', share: 18, revenue: '14,055' },
			],
			pay: [
				{ label: 'كاش', value: 74, display: '261,150', tone: 'ledger' },
				{ label: 'كارت', value: 26, display: '91,750', tone: 'bright' },
				{ label: 'إلغاءات · 38', value: 1, display: '1,520', tone: 'flag' },
			],
		},
	} satisfies Record<Range, Report>,
};

export type Cadence = 'daily' | 'weekly' | 'monthly';

export interface Summary {
	title: string;
	time: string;
	ask: string;
	lines: { dot: string; text: string }[];
}

export const summaries = {
	eyebrow: 'ملخصات بالذكاء الاصطناعي',
	example: 'الملخص ده مثال.',
	title: 'مش لازم تفتح التقرير. التقرير هيجيلك.',
	body: 'كل صبح، وكل أول أسبوع، وكل أول شهر: رسالة قصيرة فيها اللي اتغيّر بس — الأرقام اللي تستاهل تعرفها، والحاجة اللي محتاجة منك قرار.',
	label: 'الملخص',
	options: [
		{ value: 'daily', label: 'يومي' },
		{ value: 'weekly', label: 'أسبوعي' },
		{ value: 'monthly', label: 'شهري' },
	] satisfies { value: Cadence; label: string }[],
	via: 'هيوصل على',
	channels: ['واتساب', 'تليجرام', 'إيميل'],
	mark: 'بوصلة',
	openReport: 'افتح التقرير كامل',
	byCadence: {
		daily: {
			title: 'ملخص امبارح',
			time: '8:00 AM',
			ask: 'ليه الأربعاء أعلى؟',
			lines: [
				{
					dot: 'var(--signal-ledger)',
					text: 'قفلت «7,340» ج من «186» فاتورة — أعلى من الأربع أيام اللي فاتوا بـ «6٪».',
				},
				{
					dot: 'var(--signal-flag)',
					text: 'اتلغى «3» طلبات بـ «75» ج، كلهم بعد الساعة «9» بالليل.',
				},
				{
					dot: 'var(--signal-flag)',
					text: 'الرز بالشعرية خلص الساعة «9» بالليل، وده تالت يوم ورا بعض. غالبًا محتاج كمية أكبر.',
				},
				{
					dot: 'var(--signal-ledger)',
					text: 'أزحم ساعة كانت «8–9» بالليل: «41» فاتورة.',
				},
			],
		},
		weekly: {
			title: 'ملخص الأسبوع',
			time: 'SAT 9:00 AM',
			ask: 'قارن الفروع',
			lines: [
				{
					dot: 'var(--signal-ledger)',
					text: 'الأسبوع قفل «86,210» ج، أعلى من اللي قبله بـ «9٪». وسط البلد شايل «41٪» من المبيعات.',
				},
				{
					dot: 'var(--signal-flag)',
					text: 'المعادي نازل للأسبوع التاني: «−7٪». النزول كله في الضهر، والمسا ثابت.',
				},
				{
					dot: 'var(--signal-speed)',
					text: 'طاجن الفراخ بقى تاني صنف بعد ما كان الخامس. لو هتعمل عرض، ده مرشّح.',
				},
				{
					dot: 'var(--signal-ledger)',
					text: 'الإلغاءات «0.8٪» من الفواتير — أقل من الشهر اللي فات.',
				},
			],
		},
		monthly: {
			title: 'ملخص أغسطس',
			time: 'SEP 1 · 9:00 AM',
			ask: 'إيه اللي هيفرق في سبتمبر؟',
			lines: [
				{
					dot: 'var(--signal-ledger)',
					text: 'الشهر قفل «352,900» ج من «5,318» فاتورة — أعلى من يوليو بـ «12٪».',
				},
				{
					dot: 'var(--signal-ledger)',
					text: 'الجمعة لوحدها «19٪» من الشهر. التلات أضعف يوم بفرق كبير.',
				},
				{
					dot: 'var(--signal-flag)',
					text: 'فيه «4» طلبات توصيل من آخر الشهر لسه ما اتحاسبتش، بـ «620» ج.',
				},
				{
					dot: 'var(--signal-speed)',
					text: 'الدفع بالكارت طلع من «21٪» لـ «26٪» من المبيعات، والكاش اللي في الدرج قلّ معاه.',
				},
			],
		},
	} satisfies Record<Cadence, Summary>,
};

export const alerts = {
	eyebrow: 'المتابعة',
	title: 'بوصلة بتلاحظ قبلك.',
	body: 'مش كل رقم يستاهل تنبيه. بوصلة هتسكت طول ما كله ماشي، وهتتكلم لما حاجة تطلع برّه العادي بتاعك انت — مش برّه رقم ثابت حد حطّه.',
	cardTitle: 'تنبيهات النهاردة',
	cardNote: 'كل الفروع',
	items: [
		{
			dot: 'var(--signal-flag)',
			title: 'توصيل مستني الحساب',
			body: '4 طلبات توصيل في المعادي لسه ما اتدفعتش من أكتر من ساعة.',
			tag: 'تابعه',
			tone: 'flag',
			time: '4:12 PM',
		},
		{
			dot: 'var(--signal-flag)',
			title: 'إلغاء بعد الطبع',
			body: '3 فواتير اتلغت بعد ما اتطبعت في أقل من ساعة — ده مش العادي في الفرع ده.',
			tag: 'راجعه',
			tone: 'flag',
			time: '2:40 PM',
		},
		{
			dot: 'var(--signal-speed)',
			title: 'مدينة نصر مش متصل',
			body: 'آخر فاتورة وصلت من 18 دقيقة. الكاشير هناك مش هيقدر يقفل طلب لحد ما النت يرجع.',
			tag: 'دلوقتي',
			tone: 'live',
			time: '2:02 PM',
		},
		{
			dot: 'var(--signal-ledger)',
			title: 'الضهر أعلى من العادي',
			body: 'وسط البلد عدّى مبيعات الضهر المعتادة بـ 22% قبل الساعة 2.',
			tag: 'كويس',
			tone: 'ok',
			time: '1:55 PM',
		},
	] as const,
};

export type Channel = 'wa' | 'tg';

export const orders = {
	eyebrow: 'طلبات بالذكاء الاصطناعي',
	title: 'الزبون يكتب زي ما بيتكلم. الطلب يوصل الكاشير مترتّب.',
	body: 'رقم واتساب أو بوت تليجرام للمطعم. بوصلة هتفهم الرسالة بالعامية، تطابقها على المنيو بتاعك بالأسعار، وتحطها على الكاشير — ومش هتتطبع غير لما حد عندك يأكدها.',
	channelLabel: 'القناة',
	channels: [
		{ value: 'wa', label: 'واتساب' },
		{ value: 'tg', label: 'تليجرام' },
	] satisfies { value: Channel; label: string }[],
	steps: [
		'الزبون يبعت على رقم المطعم زي ما بيكلّم حد — «٢ كشري من غير بصل».',
		'بوصلة تطابق الكلام على المنيو بالأسعار، وتسأل الزبون يأكد قبل أي حاجة.',
		'الطلب يظهر على الكاشير متقطّع لحد ما حد يأكده، وبعدين يتطبع ويتسجّل زي أي فاتورة.',
	],
	restaurant: 'كشري أبو علي',
	initial: 'ك',
	back: 'رجوع',
	call: 'اتصال',
	more: 'المزيد',
	attach: 'إرفاق',
	reading: 'بيقرا',
	readingCaption: 'بتقرا الطلب',
	typing: 'بوصلة بتكتب…',
	replyingWa: 'بيرد عليك بوصلة · واتساب',
	replyingTg: 'بيرد عليك بوصلة · تليجرام',
	channelNoteWa: 'رقم المطعم على واتساب',
	channelNoteTg: '@abuali_bot على تليجرام',
	message1:
		'عايز ٢ كشري كبير واحد منهم من غير بصل، وطاجن فراخ، وبيبسي. العنوان ١٢ شارع النصر، الدور التالت',
	draft1: 'عايز ٢ كشري كبير واحد منهم من غير بصل…',
	replyStart: 'تمام، ده طلبك:',
	totalLabel: 'الإجمالي + التوصيل',
	total: '262 + 15',
	replyEnd: 'على ١٢ شارع النصر، الدور التالت. أأكّد؟',
	message2: 'تمام، أكّد',
	confirmedStart: 'المطعم أكّد طلبك. هيوصلك في حوالي ',
	confirmedMinutes: '35',
	confirmedEnd: ' دقيقة.',
	placeholder: 'اكتب رسالة',
	send: 'إرسال',
	voice: 'رسالة صوتية',
	lines: [
		{ qty: '2', name: 'كشري كبير', note: 'واحد من غير بصل', price: '90' },
		{ qty: '1', name: 'طاجن فراخ', note: '', price: '150' },
		{ qty: '1', name: 'بيبسي', note: '', price: '22' },
	],
	cashierTitle: 'على الكاشير',
	cashierColumns: [
		{ label: 'العدد', w: 'minmax(0,.4fr)' },
		{ label: 'الصنف', w: 'minmax(0,1.6fr)' },
		{ label: 'ج', w: 'minmax(0,.6fr)' },
	],
	cashierEmpty: 'مفيش طلبات جديدة من الشات',
	noteWaiting: 'من الشات',
	noteUnconfirmed: 'مستني تأكيد',
	noteDone: 'اتأكد · اتطبع',
	confirmPrint: 'أكّد واطبع · 262',
	printed: 'اتطبع',
	hintWaiting: 'مستني رسالة.',
	hintUnconfirmed: 'الحرف المتقطّع يعني لسه محدش أكّد.',
	hintDone: 'بقى فاتورة عادية في التقارير.',
};

export const features = {
	title: 'وحاجات تانية بتوضّح الصورة',
	items: [
		{
			title: 'سجل كل حاجة',
			body: 'كل إلغاء وكل فتحة درج — باسم الكاشير والوقت. ومفيش طلب بيتمسح.',
			soon: false,
		},
		{
			title: 'صلاحيات بالظبط',
			body: 'كل كاشير بكود لوحده، والدرج مابيفتحش من غير بيع إلا بكود مدير. وانت بتشوف كل الفروع من حسابك.',
			soon: false,
		},
		{
			title: 'مقارنة الفروع',
			body: 'كل فرع جنب التاني بنفس الأرقام ونفس المدة، فالفرق يبان من غير حسابات.',
			soon: true,
		},
		{
			title: 'ربح كل صنف',
			body: 'حط تكلفة المكونات مرة، وبوصلة تقولك كل طبق بيكسب كام فعلاً — مش بس بيتباع كام.',
			soon: false,
		},
		{
			title: 'توقّع الكميات',
			body: 'من مبيعات الأسابيع اللي فاتت: هتحتاج رز قد إيه يوم الجمعة.',
			soon: true,
		},
		{
			title: 'تصدير للمحاسب',
			body: 'ملف Excel بالمبيعات آخر كل شهر، بالشكل اللي المحاسب بيطلبه.',
			soon: true,
		},
	],
};

export const faq = {
	title: 'الذكاء بيقرأ، مابيألّفش.',
	body: 'كل إجابة وكل ملخص هيبقى معاه الفواتير اللي طلع منها. لو السؤال مالوش إجابة في أرقامك، بوصلة هتقولك إنها مش عارفة — مش هتخترع رقم يطمّنك.',
	items: [
		{
			q: 'بياناتي بتروح فين؟',
			a: 'على حسابك بس، ومحدش من مطعم تاني يقدر يشوفها. وكل فاتورة بتفضل متسجلة، حتى اللي اتلغت.',
		},
		{
			q: 'بيشتغل على أي كمبيوتر؟',
			a: 'أي كمبيوتر ويندوز عليه كروم، وطابعة فواتير حرارية متعرّفة عليه. شاشة لمس أحسن، بس الماوس بتعمل نفس الشغل.',
		},
		{
			q: 'لازم انترنت؟',
			a: 'أيوه. الطلب اللي بتكتبه بيفضل محفوظ لو النت قطع، بس عشان تقفله وتطبعه لازم النت يرجع.',
		},
		{
			q: 'محتاج حاجة غير الكاشير؟',
			a: 'لأ. التقارير بتتبني من الفواتير اللي الكاشير بيطبعها أصلاً.',
		},
		{
			q: 'الملخص هيبقى طويل؟',
			a: 'أربع سطور تقريبًا. هيقول اللي اتغيّر بس، ولو كله عادي هيقولك كده في سطر.',
		},
	],
};

export const closing = {
	title: 'أول تقرير هيبقى جاهز بعد أول فاتورة.',
	body: 'سجّل النهاردة، بيع بالليل، وبكرة الصبح افتح التقرير من أي متصفح. ومش بنطلب كارت.',
};

export const pricing = {
	eyebrow: 'الأسعار',
	titleStart: 'مجانا ',
	titleMark: 'طول فترة التجربة',
	titleEnd: '.',
	titleLine2: 'وبعد كده هنقولك قبل ما تدفع.',
	lead: 'مش هنخترع رقم عشان نبيعك النهاردة. السعر النهائي لسه مش متحدد — وأول ما يتحدد هتعرفه على الصفحة دي وفي إيميل، قبل ما تدفع أي حاجة.',
	noCard: 'مش بنطلب كارت، فمفيش حاجة تتسحب لوحدها',
	pay: {
		eyebrow: 'اللي بتدفعه لبوصلة',
		value: 'صفر',
		sub: 'طول فترة التجربة',
		includes: [
			'كل أصناف المنيو، مفيش حد',
			'كاشيرية بعدد ما تحتاج، كل واحد بكود',
			'طبع فواتير عربي، ومفيش حد للأوردرات',
			'تقارير المبيعات والأصناف والإلغاءات',
			'فرع واحد أو أكتر على نفس الحساب',
		],
	},
	hardware: {
		eyebrow: 'اللي ممكن تدفعه مرة واحدة',
		value: 'الطابعة بس',
		sub: 'وده لو مش عندك طابعة فواتير حرارية — تشتريها من السوق',
		items: [
			'الكمبيوتر اللي على الكاونتر: مجانا، أي كمبيوتر ويندوز عندك بينفع',
			'طابعة حرارية 80 مم: أي موديل ليه تعريف على ويندوز',
			'درج كاش: اختياري، بيتوصل في الطابعة وتضيفه بعدين',
			'شاشة لمس: اختياري، الماوس بتكفي',
		],
		note: 'مفيش رسوم تركيب، ومفيش جهاز مربوط بالحساب.',
	},
	after: {
		title: 'هيحصل إيه لما التجربة تخلص؟',
		leadStart: 'أوضح حاجة نقدر نقولها دلوقتي هي اللي ',
		leadStrong: 'مش',
		leadEnd: ' هيحصل.',
		cards: [
			{
				title: 'مفيش سحب تلقائي',
				body: 'إحنا مش ماخدين كارت منك من الأساس، فمفيش حاجة تتسحب.',
				soon: false,
				invert: true,
			},
			{
				title: 'هنبلّغك قبل ما تدفع',
				body: 'إيميل بالسعر، وقرار الاستمرار قرارك انت.',
				soon: false,
				invert: false,
			},
			{
				title: 'السعر مش بالفرع الواحد المخفي',
				body: 'أول ما نعلنه، هيكون رقم مكتوب على الصفحة دي، مش عرض سعر بعد مكالمة.',
				soon: false,
				invert: false,
			},
		],
	},
	hidden: {
		title: 'ليه السعر مخفي عند الباقي؟',
		usualTitle: 'الطريقة المعتادة',
		usualBody:
			'بتدوّر على السعر، مبتلاقيهوش. بتسيب رقمك، بيكلّموك، وبعدين بيجيلك عرض على قد ما شكل محلك بيسمح.',
		oursTitle: 'عندنا',
		oursBody:
			'بتسجّل بنفسك. بتشوف المنتج قبل ما تتكلم مع حد. ولو مش عاجبك، بتسيبه من غير ما تعتذر لحد.',
		oursValue: 'صفر طول التجربة',
		oursSub: 'وبعدها سعر معلن، مش عرض سعر',
	},
	questions: {
		title: 'أسئلة عن الفلوس',
		items: [
			{
				q: 'في نسخة مجانية للأبد؟',
				a: 'مش بنوعد بكده. اللي بنوعد بيه: مجانا طول فترة التجربة، وسعر معلن قبل ما تدفع أي حاجة.',
			},
			{
				q: 'في حد للأوردرات في اليوم؟',
				a: 'لأ. بيع زي ما انت عايز — المجاني مش مقصوص.',
			},
			{
				q: 'عندي فرعين، بتحسبوا إزاي؟',
				a: 'في التجربة، الفروع كلها داخلة. طريقة حساب الفروع هنعلنها مع السعر.',
			},
			{
				q: 'لازم أخد الهاردوير منكم؟',
				a: 'لأ خالص. بوصلة شغالة على الكمبيوتر اللي عندك، وأي حاجة ناقصة تشتريها من السوق.',
			},
		],
	},
	closing: {
		title: 'أسهل طريقة تعرف بوصلة تنفعك: تجرّبها.',
		body: 'دقيقة تسجيل، ومفيش كارت. لو الكمبيوتر على الكاونتر شغال، تقدر تبيع عليه الليلة.',
	},
};

// The privacy page states what the product does with data today, from the
// code: the Google scopes, the stored fields, the cookies and their
// lifetimes, the log and backup retention. How it changes: CLAUDE.md,
// Definition of done.
export interface PrivacySection {
	title: string;
	paragraphs: string[];
	items?: string[];
	after?: string[];
}

export const privacy = {
	eyebrow: 'الخصوصية',
	title: 'سياسة الخصوصية',
	lead: 'الصفحة دي بتقول إيه البيانات اللي بوصلة بتشوفها، وبتعمل بيها إيه، وفين بتتخزن.',
	updated: 'آخر تحديث: «7» أكتوبر «2026»',
	contact: 'لو عندك سؤال أو طلب عن بياناتك، ابعت على',
	email: 'data@bosla.shop',
	sections: [
		{
			title: 'الموقع ده',
			paragraphs: [
				'الموقع ده مابيحطّش كوكيز، ومافيهوش إعلانات ولا أدوات تتبّع ولا تحليلات. الخطوط متخزنة عندنا، فمفيش طلب بيروح لجهة تانية وانت بتتصفح.',
				'الموقع متخزن على Cloudflare، وهي بتشوف عنوان الـ IP بتاعك عشان توصّلك الصفحة، زي أي استضافة.',
			],
		},
		{
			title: 'لما تسجّل دخول',
			paragraphs: [
				'تقدر تدخل لوحة التحكم بحساب جوجل، أو بإيميل وباسورد.',
				'لو بتدخل بجوجل، بناخد منهم اسمك وإيميلك ورقم حسابك عندهم بس، ومابنطلبش أي صلاحية تانية. مابنحتفظش بأي مفتاح دخول من جوجل، فمانقدرش نوصل لحسابك عندهم. وكل مرة تسجّل دخول، الاسم والإيميل بيتحدّثوا من جوجل.',
				'لو بتدخل بإيميل وباسورد، الباسورد مابيتخزنش زي ما هو: بيتخزن بس كـ hash بطيء ومملّح (Argon2id)، فماحدش يقدر يقراه، ولا إحنا.',
				'بنبعتلك إيميلات من no-reply@bosla.shop عن طريق Amazon SES: لينك يخلّص التسجيل ويشتغل يوم، أو لينك تختار بيه باسورد جديد ويشتغل «30» دقيقة ومرة واحدة، أو تنبيه إن الإيميل ده عليه حساب بالفعل. التسجيل مابيعملش حساب غير لما تفتح اللينك.',
				'من «طرق الدخول» في الإعدادات تقدر تضيف جوجل أو باسورد لحسابك، أو تشيل واحدة منهم. أي طريقة تتضاف بنبعت عنها إيميل لعنوانك، عشان لو حد غيرك ضافها تاخد بالك. ولما تختار باسورد جديد من لينك، بتخرج من أي جهاز تاني.',
				'كل محاولة دخول بإيميل وباسورد، وكل إيميل بنبعته، بنسجّلهم بـ hash للإيميل (SHA-256) مع عنوان الـ IP، عشان نوقف اللي بيجرّب باسوردات أو بيبعت إيميلات كتير.',
			],
		},
		{
			title: 'بيانات مطعمك',
			paragraphs: ['بنخزن اللي انت والكاشيرية بتدخّلوه عشان الكاشير ولوحة التحكم يشتغلوا:'],
			items: [
				'اسم المطعم والفروع والمنيو والأسعار والإعدادات.',
				'أسامي الكاشيرية ودور كل واحد. كود الدخول بيتخزن بطريقة مايتقريش حتى لينا.',
				'الطلبات والفواتير والإلغاءات وفتح الدرج، بالوقت واسم اللي عملها. الطلبات مابتتمسحش، لأنها سجل مبيعات مطعمك.',
				'وقت كل محاولة دخول بكود على الكاشير وعنوان الـ IP بتاعها، عشان نوقف اللي بيجرّب أكواد.',
			],
		},
		{
			title: 'صفحة مطعمك في الكتالوج',
			paragraphs: [
				'لو نشرت مطعمك، صفحته بتبقى عامة على menu.bosla.shop: اسم المطعم، والمنطقة واللوجو اللي حطيتهم، وأسامي فروعك وعناوينها ومكانها على الخريطة، والمنيو بأسعار كل فرع واللي خلص منه. أي حد يقدر يقراها، ومحركات البحث كمان.',
				'لو شلت النشر، أو الحساب اتوقف، الصفحة بتنزل على طول. ولو غيّرت اللوجو، القديم بيفضل متخزن عندنا.',
				'اللي بيزور الكتالوج مابيسجّلش دخول، ومابنحطّلوش كوكيز، ومابيتخزنش عنه حاجة. الكتالوج متخزن على Cloudflare، وهي بتشوف عنوان الـ IP بتاعه زي أي استضافة. ولو فتح فرع على Google Maps، بيروح لجوجل وبيمشي على شروطهم.',
				'ولما تحط مكان فرعك على الخريطة في لوحة التحكم، الخريطة بتتحمّل من سيرفرات OpenStreetMap.',
			],
		},
		{
			title: 'بيانات زباين المطعم',
			paragraphs: [
				'لما الكاشير يسجّل طلب توصيل، بيتخزن اسم الزبون ورقمه وعنوانه والعلامة المميزة لو اتكتبت، وبيتطبعوا على الفاتورة. الزبون بيتحفظ عشان الكاشير يلاقيه المرة الجاية.',
				'صاحب المطعم يقدر يمسح أي زبون من لوحة التحكم، والطلبات القديمة بتفضل بالعنوان اللي اتطلبت عليه. لو انت زبون مطعم بيستخدم بوصلة وعايز بياناتك تتمسح، كلّم المطعم أو ابعتلنا.',
			],
		},
		{
			title: 'لما تسيب بياناتك في إعلان لبوصلة',
			paragraphs: [
				'إعلانات بوصلة على فيسبوك وإنستجرام بتخليك توصلّنا بطريقة من اتنين: فورم تسيب فيه بياناتك عشان نكلّمك، أو زرار بيفتح شات مع فريق بوصلة على الواتساب.',
				'لو بعت الفورم، Meta بتبعتلنا اللي كتبته فيه بس:',
			],
			items: [
				'اسمك ورقم موبايلك.',
				'اسم المطعم ومنطقته وعدد فروعه، ودورك فيه، والسيستم اللي بتستخدمه دلوقتي.',
				'انهي إعلان بعت منه الفورم، ووقت ما بعته.',
			],
			after: [
				'ولو كلّمتنا على الواتساب من إعلان، بنحتفظ باسمك ورقمك، وانهي إعلان جيت منه ووقت ما كلّمتنا، واللي بتقولهولنا في الشات عن مطعمك: اسمه ومنطقته وعدد فروعه ودورك فيه.',
				'بنستخدم البيانات دي عشان نكلّمك على الواتساب بخصوص تجربة بوصلة، وعشان نعرف انهي إعلان جاب مطاعم أكتر. مابنرجّعش رقمك لـ Meta، ومابنستخدمهوش في أي إعلان.',
				'بتتخزن على جهاز فريق بوصلة، ورسايل الواتساب بتتخزن على سيرفرنا على Amazon Web Services في فرانكفورت. بتفضل محفوظة لحد ما تطلب مسحها: قولّنا في نفس شات الواتساب، أو ابعت على data@bosla.shop. ولو قلتلنا مش مهتم، مش هنكلّمك تاني.',
				'وMeta نفسها بتشوف الفورم وبتحتفظ بيه حسب سياستها هي، لأنه اتملى على فيسبوك أو إنستجرام، وبتعرف إنك فتحت شات معانا من إعلانها.',
			],
		},
		{
			title: 'الكوكيز في الكاشير ولوحة التحكم',
			paragraphs: ['الكاشير ولوحة التحكم بيستخدموا كوكيز للدخول بس، مش للتتبع:'],
			items: [
				'تسجيل الدخول بجوجل، أو إضافة جوجل لحسابك: «10» دقايق، وبتتمسح أول ما ترجع من جوجل.',
				'دخول صاحب المطعم: «7» أيام.',
				'ربط جهاز الكاشير بالفرع: «365» يوم.',
				'دخول الكاشير: «12» ساعة، أو المدة اللي المطعم يحددها.',
			],
			after: [
				'وجهاز الكاشير بيحتفظ بالطلب اللي لسه بيتكتب، عشان مايضيعش لو النت قطع، وباختيار اللغة والشكل.',
			],
		},
		{
			title: 'فين بتتخزن',
			paragraphs: [
				'البيانات متخزنة على Amazon Web Services في فرانكفورت بألمانيا. قاعدة البيانات متشفّرة ومش متاحة على الإنترنت، وكل مطعم بياناته منفصلة: مطعم مايقدرش يشوف بيانات مطعم تاني.',
				'فيه نسخة احتياطية تلقائية نقدر نرجّع منها لأي لحظة في آخر «14» يوم. سجلات السيرفر، ومنها عناوين الـ IP، بتتمسح بعد «30» يوم.',
				'الإيميلات اللي بتتبعت لأي عنوان على bosla.shop، ومنها data@bosla.shop، بتتخزن كاملة هناك برضه، وبتفضل محفوظة لحد ما نمسحها.',
			],
		},
		{
			title: 'مين تاني بيشوفها',
			paragraphs: [
				'مابنبيعش بياناتك، ومابنستخدمهاش في إعلانات. الجهات الوحيدة اللي بتتعامل معاها هي اللي بتشغّل الخدمة: Amazon Web Services للسيرفر وقاعدة البيانات والإيميل، وCloudflare للموقع والكتالوج وملفات الكاشير، وGoogle لتسجيل الدخول لو اخترته، وOpenStreetMap للخريطة في لوحة التحكم. ولو جيت من إعلان لبوصلة، Meta بتشوف الفورم اللي بعته أو الشات اللي فتحته منه، والواتساب بيوصّل الرسايل بينا.',
			],
		},
		{
			title: 'حقك',
			paragraphs: [
				'تقدر تطلب نسخة من بياناتك، أو تصلّح حاجة غلط فيها، أو تطلب مسحها. ابعت الطلب من الإيميل اللي بتسجّل بيه، عشان نتأكد إنه انت.',
			],
		},
		{
			title: 'لو الصفحة دي اتغيّرت',
			paragraphs: ['لو غيّرنا حاجة هنا، هنكتب التاريخ الجديد فوق.'],
		},
	] satisfies PrivacySection[],
};
